import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

import { NoServerConnectionDialog } from '../../components/dialogs/no-server-connection/no-server-connection.dialog';
import { ServerResponseComponent } from '../../components/dialogs/server-response/server-response.component';
import { ActionResultModel } from '../../models/core/action-result.model';
import { ApiResponseModel } from '../../models/core/api-response.model';
import { BusinessExceptionModel } from '../../models/core/business-exception.model';
import { AlertService } from '../alert.service';
import { AuthService } from '../auth.service';
import { ErrorsService } from '../errors.service';
import { LoggerService } from '../logger.service';
import { NotificationService } from '../notification.service';
import { AppSessionStorage } from '../storage/app.session-storage';

@Injectable()
export class ResponseHandlerHttpInterceptor implements HttpInterceptor {
    private hasServerConnection = true;

    private readonly loggerService = inject(LoggerService);
    private readonly alertService = inject(AlertService);
    private readonly notificationService = inject(NotificationService);
    private readonly errorsService = inject(ErrorsService);
    private readonly authService = inject(AuthService);
    private readonly appSessionStorage = inject(AppSessionStorage);
    private readonly dialog = inject(MatDialog);

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        let serverError: any;
        let hasError = false;
        let body: any;

        return next.handle(request).pipe(
            tap(
                event => {
                    if (event instanceof HttpResponse && event.body) {
                        body = event.body;
                    }
                },
                error => {
                    hasError = true;
                    serverError = error;
                }
            ),
            finalize(() => {
                if (body && typeof body.type === 'string' && body.type.startsWith('ActionResult')) {
                    const bodyResponse = body as ApiResponseModel<ActionResultModel>;

                    if (!!bodyResponse.value.message) {
                        if (!bodyResponse.relatedActions || bodyResponse.relatedActions.length < 1) {
                            this.notificationService.showInfo(bodyResponse.value.message);
                        } else {
                            this.showServerResponseDialog(bodyResponse);
                        }
                    }

                    if (bodyResponse.value.isSuccessful) {
                        return;
                    }
                }

                if (hasError) {
                    if (serverError.status === 0 && serverError.error.type !== 'abort') {
                        // the server could not be reached, because there is no internet or the server is down
                        if (this.hasServerConnection) {
                            this.showNoInternetDialog();
                        }
                        this.hasServerConnection = false;
                        return;
                    } else {
                        this.hasServerConnection = true;
                    }

                    switch (serverError.status) {
                        case 401:
                        case 403:
                            this.dialog.closeAll();
                            this.appSessionStorage.saveNotLoggedRedirectUrl(window.location.href);
                            this.authService.logout(false);
                            break;
                        case 500:
                            this.loggerService.error(serverError.error.message);
                            this.alertService.showError(serverError.error.message, undefined, true);
                            break;
                        case 588:
                            let bizErrors: BusinessExceptionModel[] = [];
                            let content = serverError.error.message || serverError.error.value.message;

                            if (!!serverError.error.value?.errors) {
                                bizErrors = serverError.error.value.errors;
                                content = '<table class="table table-hover"><tbody>';

                                bizErrors.forEach(e => (content += `<tr><td>${e.property}</td><td>${e.error}</td></tr>`));
                                content += '</tbody></table>';
                            }

                            this.errorsService.setBusinessExceptions(bizErrors);

                            if (this.errorsService.shouldDisplayAlertOnError) {
                                this.alertService.showAlert(content, undefined, true, true);
                            }

                            break;
                    }
                }
            })
        );
    }

    private showServerResponseDialog(result: ApiResponseModel<ActionResultModel>): void {
        const dialogRef = this.dialog.open(ServerResponseComponent, {
            width: '450px',
            data: result
        });

        dialogRef.afterClosed().subscribe(() => {
            // TODO: see if we'll add any routing logic here, if not, remove the subscription.
        });
    }

    private showNoInternetDialog(): void {
        this.dialog.open(NoServerConnectionDialog, {
            disableClose: true
        });
    }
}
